import { subject } from '@casl/ability';
import { useTranslation } from 'react-i18next';
import { ActionsTableMenu } from 'src/components/atoms/actions-table-menu';
import { usePermissions } from 'src/permissions/can';
import { UserRole } from 'src/types/enum/user-role';
import { AdminInvitationStatus } from '../../types/enum/admin-invitation-status';

interface Props {
  onEditPhoneNumberActionClick: () => void;
  onEditRoleActionClick: () => void;
  onResendInvitationActionClick: () => void;
  onRemoveAdminActionClick: () => void;
  adminToken: string;
  adminRole: UserRole;
  isCurrentAdminSelfIdentityEqual: boolean;
  invitationStatus: AdminInvitationStatus;
}

const AdminsActionsTableMenu = ({
  onEditPhoneNumberActionClick,
  onEditRoleActionClick,
  onRemoveAdminActionClick,
  onResendInvitationActionClick,
  isCurrentAdminSelfIdentityEqual,
  adminToken,
  adminRole,
  invitationStatus,
}: Props) => {
  const { t } = useTranslation();
  const permissions = usePermissions();

  const actions = [
    {
      id: 'edit-phone-number',
      label: t('organizationDetails:admins.editPhoneNumber'),
      onClick: onEditPhoneNumberActionClick,
      disabled: permissions.cannot(
        'updatePhoneNumber',
        subject('TenantAdministrators', {
          userToken: adminToken,
          role: adminRole,
        }),
      ),
      tooltip: t('common:notSufficientPermissions'),
    },
    {
      id: 'edit-role',
      label: t('organizationDetails:admins.editRole'),
      onClick: onEditRoleActionClick,
      disabled: permissions.cannot(
        'updateRole',
        subject('TenantAdministrators', {
          userToken: adminToken,
          role: adminRole,
        }),
      ),
      tooltip: isCurrentAdminSelfIdentityEqual
        ? t('common:youCantChangeYourRole')
        : t('common:notSufficientPermissions'),
    },
    {
      id: 'delete',
      label: t('organizationDetails:admins.delete'),
      onClick: onRemoveAdminActionClick,
      disabled: permissions.cannot(
        'delete',
        subject('TenantAdministrators', {
          userToken: adminToken,
          role: adminRole,
        }),
      ),
      tooltip: isCurrentAdminSelfIdentityEqual
        ? t('common:youCantRemoveYourself')
        : t('common:notSufficientPermissions'),
    },
    {
      id: 'resend-invitation',
      label: t('organizationDetails:admins.resendInvitation'),
      onClick: onResendInvitationActionClick,
      disabled:
        permissions.cannot(
          'resendInvitation',
          subject('TenantAdministrators', {
            userToken: adminToken,
            role: adminRole,
          }),
        ) || invitationStatus !== AdminInvitationStatus.PENDING,
      tooltip: isCurrentAdminSelfIdentityEqual
        ? t('common:youCantResendInvitationToYourself')
        : invitationStatus !== AdminInvitationStatus.PENDING
          ? t('organizationDetails:admins.invitationAlreadyAcceptedTooltip')
          : t('common:notSufficientPermissions'),
    },
  ];

  return <ActionsTableMenu actions={actions} />;
};

export default AdminsActionsTableMenu;
